/* =====================================================================================
   Sign in.

   Two routes, because they fail in different ways and one covers the other:
   a password, and a single-use link for when the password has gone.

   What this screen deliberately does NOT do:
     * confirm whether an address has an account (that is a membership oracle)
     * offer self-service registration (members are created by an administrator; a
       diagnostics company's system is not something people sign themselves up to)
     * remember the password anywhere but the browser's own credential manager
   ===================================================================================== */

import { useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import './signin.css';
import { TENANT } from '@tenant';

interface Props {
  productName: string;
  orgName: string;
}

export function SignIn({ productName, orgName }: Props) {
  const { signInWithPassword, sendMagicLink } = useAuth();
  const [mode, setMode] = useState<'password' | 'link'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'password') {
        await signInWithPassword(email, password);
        // No navigation here. The session change propagates through AuthProvider and the
        // gate swaps the shell in, so there is one source of truth for "signed in".
      } else {
        await sendMagicLink(email);
        setSent(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <div className="signin-brand">
        <div className="signin-brand-inner">
          {/* The real mark, matching the one in the shell: three broken concentric rings
              around a centre point. The sign-in screen is the first thing anyone sees, so it
              must be the actual logo rather than an approximation of it. Rendered larger here
              and using the same geometry as public/icon.svg. */}
          <div className="signin-mark" aria-hidden="true">
            <svg viewBox="0 0 100 100" width="60" height="60">
              <g transform="translate(50,50) rotate(-40)" fill="none" strokeLinecap="round">
                <circle r="40" stroke="#fff" strokeOpacity="0.28" strokeWidth="6" strokeDasharray="211 40" />
                <circle r="28" stroke="#fff" strokeOpacity="0.5" strokeWidth="7" strokeDasharray="148 28" />
                <circle r="17" stroke="var(--brand-accent)" strokeWidth="8" strokeDasharray="90 17" />
              </g>
              <circle cx="50" cy="50" r="6.5" fill="var(--brand-accent)" />
            </svg>
          </div>
          <h1 className="signin-product">{productName}</h1>
          <p className="signin-org">{orgName}</p>
          <p className="signin-statement">{TENANT.vocabulary.statement}</p>
        </div>
        <p className="signin-footnote">
          A Patient Thread product. Data held in the United Kingdom.
        </p>
      </div>

      <div className="signin-panel">
        <form className="signin-form" onSubmit={submit} noValidate>
          <h2 className="signin-title">Sign in</h2>
          <p className="signin-sub">
            {mode === 'password'
              ? 'Use your work email address.'
              : 'We will email you a single-use link. It expires in one hour.'}
          </p>

          <label className="signin-label" htmlFor="email">Email</label>
          <input
            id="email" className="signin-input" type="email" value={email}
            onChange={(e) => { setEmail(e.target.value); setSent(false); }}
            autoComplete="username" autoFocus required
            placeholder="you@scirabio.com" disabled={busy}
          />

          {mode === 'password' && (
            <>
              <label className="signin-label" htmlFor="password">Password</label>
              <input
                id="password" className="signin-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" required disabled={busy}
              />
            </>
          )}

          {error && <p className="signin-error" role="alert">{error}</p>}

          {sent && (
            <p className="signin-sent" role="status">
              If that address belongs to a member, a sign-in link is on its way. Open it on
              this device.
            </p>
          )}

          <button className="signin-submit" type="submit" disabled={busy || !email}>
            {busy
              ? (mode === 'password' ? 'Signing in' : 'Sending')
              : (mode === 'password' ? 'Sign in' : 'Email me a link')}
          </button>

          <button
            className="signin-alt" type="button" disabled={busy}
            onClick={() => { setMode(mode === 'password' ? 'link' : 'password'); setError(null); setSent(false); }}
          >
            {mode === 'password' ? 'Sign in with an email link instead' : 'Use a password instead'}
          </button>

          <p className="signin-help">
            Accounts are created by an administrator. If you do not have one, ask your
            Forum owner rather than registering.
          </p>
        </form>
      </div>
    </div>
  );
}
